import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Button, Card, CardHeader, MenuItem, Stack, Typography } from '@mui/material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Grid from '@mui/system/Unstable_Grid';

import { useParams, useRouter } from 'src/routes/hooks';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { PageOptions } from 'src/services/api.types';
import { useGetContactByIdQuery, useGetContactCompanyLinksQuery } from 'src/services/contacts/contacts.service';

import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

import { Box } from '@mui/system';
import { enqueueSnackbar } from 'notistack';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import Iconify from 'src/components/iconify';
import ContactDocumentsView from 'src/sections/contacts/components/contact-documents';
import { ContactDetailSection } from 'src/sections/contacts/components/detail-card';
import { ContactProfileSection } from 'src/sections/contacts/components/profile-card';
import { Contact, ContactLinkEntityType } from 'src/services/contacts/contacts.types';
import { EmailMessageTemplateType, MailerTargetType } from 'src/services/email-sync/email-sync.types';
import { openSingleMailerDialog, openTaskEditorSidesheet } from 'src/store/slices/app/appSlice';
import { useAppDispatch } from 'src/store/store';
import { toggleCandidateEditorSidesheet } from 'src/store/slices/candidate/candidateSlice';

import AccountOwnerCard from 'src/sections/contacts/components/account-owner-card';
import CandidateLinkCard from 'src/sections/contacts/components/candidate-link-card';
import LinkedCompaniesCard from 'src/sections/contacts/components/linked-companies-card';
import { LinkedContactsCard } from 'src/sections/contacts/components/linked-contacts-card';
import { TenantType } from 'src/types/enums';

// ----------------------------------------------------------------------

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 10,
};

enum ContactsTab {
  DETAIL = 'detail',
  PROFILE = 'profile',
  DOCUMENTS = 'documents',
  COMPANIES = 'companies'
}

export default function ContactsDetailView() {

  const showTable = useBoolean();

  const { t } = useTranslate();

  const { contact_id } = useParams();

  const optionsPopover = usePopover();

  const dispatch = useAppDispatch();

  const [currentTab, setCurrentTab] = useState<ContactsTab | undefined>();

  const [searchParams, setSearchParams] = useSearchParams();

  const router = useRouter();

  const tenantType = useOrgTenant();

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (currentTab) return;

    let defaultTab: ContactsTab = ContactsTab.DETAIL

    const existingTab = searchParams.get('tab');
    if (existingTab && Object.values(ContactsTab).includes(existingTab as ContactsTab)) {
      defaultTab = existingTab as ContactsTab;
    }

    setCurrentTab(defaultTab);

  }, [currentTab, tenantType, searchParams]);

  const { currentData: contact } = useGetContactByIdQuery(contact_id as string, {
    skip: !contact_id,
  });

  const { data: contactCompanyLinks } = useGetContactCompanyLinksQuery({
    contact_id: contact_id as string,
    params: DEFAULT_PAGE,
  }, {
    skip: !contact_id,
  });

  const linkedCompanyName = contactCompanyLinks?.results?.[0]?.company_profile?.company_name ?? contact?.company_name;

  const handleChangeTab = useCallback((_event: React.SyntheticEvent, newValue: ContactsTab) => {
    setCurrentTab(newValue);
  }, []);


  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (!currentTab) return;

    setSearchParams({ "tab": currentTab }, { replace: true })

  }, [currentTab, setSearchParams]);

  const renderHead = (
    <Card sx={{ flexGrow: 0, boxShadow: 'unset', pb: 1 }}>
      <CardHeader
        sx={{ pl: 0, width: '100%' }}
        title={
          <Stack direction="column" justifyContent="start" alignItems="start" spacing={1}>
            <TruncatedTextField
              text={contact ? _.capitalize(`${contact.first_name} ${contact.last_name}`) : "Loading..."}
              limit={40}
              mode='characters'
              textTransform="capitalize"
              variant="h3"
              sx={{ mb: 0, lineHeight: 1 }}
              showMoreEnabled={false}
            />

            <Stack direction="row" spacing={1} alignItems="start" justifyContent="start" width="100%">

              {
                linkedCompanyName && (
                  <Box>
                    <TruncatedTextField
                      text={linkedCompanyName}
                      limit={40}
                      mode='characters'
                      variant="subtitle2"
                      showMoreEnabled={false}
                    />
                  </Box>
                )
              }

              {
                contact?.contact_type && (
                  <>
                    <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>-</Typography>
                    <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>{t(`enums.contact_type.${contact?.contact_type}`)}</Typography>
                  </>
                )
              }

            </Stack>

          </Stack>
        }
        action={
          <Stack direction="row" spacing={.5} alignItems="center" mt={2}>

            <Button
              variant="outlined"
              endIcon={<Iconify icon="carbon:overflow-menu-horizontal" />}
              sx={{ mr: 1 }}
              onClick={optionsPopover.onOpen}
            >
              {t('common.actions')}
            </Button>

          </Stack>
        }
      />
    </Card>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab} sx={{ pl: 2, mb: 2 }}>

      <Tab
        key={ContactsTab.DETAIL}
        iconPosition="end"
        value={ContactsTab.DETAIL}
        label={t('contacts.detail.tabs.detail')}
      />

      <Tab
        key={ContactsTab.PROFILE}
        iconPosition="end"
        value={ContactsTab.PROFILE}
        label={t('contacts.detail.tabs.profile')}
      />

      <Tab
        key={ContactsTab.DOCUMENTS}
        iconPosition="end"
        value={ContactsTab.DOCUMENTS}
        label={t('contacts.detail.tabs.documents')}
      />

    </Tabs>
  );

  const handleCopyToClipboard = (text: string) => {
    optionsPopover.onClose();

    if (!text) return;

    navigator.clipboard.writeText(text ?? "" as string);
    enqueueSnackbar(t('common.copied_to_clipboard'), { variant: 'success' });
  }

  const handleSendEmail = useCallback(() => {
    if (!contact || !contact.email) return;

    dispatch(openSingleMailerDialog({
      emails: [contact.email],
      templateType: EmailMessageTemplateType.CONTACT_ENGAGEMENT,
      templateEntityId: contact.id,
      targetType: MailerTargetType.CONTACT,
      targetId: contact.id
    }));
  }, [dispatch, contact]);

  const handleAddTask = useCallback(() => {
    dispatch(openTaskEditorSidesheet({
      contactId: contact_id,
    }))
  }, [dispatch, contact_id]);

  const handleCreateCandidate = useCallback(() => {
    if (!contact) return;

    dispatch(toggleCandidateEditorSidesheet({
      status: true,
      candidateId: null,
      firstName: contact.first_name,
      lastName: contact.last_name,
      email: contact.email,
    }));
  }, [dispatch, contact]);

  return (
    <>
      <Grid container>
        <Grid xs={12}>
          {renderHead}
        </Grid>
      </Grid>

      {renderTabs}

      <Grid container spacing={2}>
        <Grid xs={12} md={8}>
          {currentTab === ContactsTab.PROFILE && (
            <ContactProfileSection contactId={contact_id as string} />
          )}

          {currentTab === ContactsTab.DETAIL && (
            <ContactDetailSection contactId={contact_id as string} />
          )}

          {currentTab === ContactsTab.DOCUMENTS && (
            <ContactDocumentsView contactId={contact_id as string} />
          )}
        </Grid>
        <Grid xs={12} md={4}>
          <Stack direction="column" spacing={2}>

            <AccountOwnerCard contactId={contact_id as string} />

            <CandidateLinkCard contactId={contact_id as string} />

            <LinkedCompaniesCard contactId={contact_id as string} />

            <LinkedContactsCard entityId={contact_id as string} entityType={ContactLinkEntityType.CONTACT} />

          </Stack>

        </Grid>
      </Grid>
      <CustomPopover
        open={optionsPopover.open}
        onClose={optionsPopover.onClose}
        arrow="top-right"
      >
        {
          contact?.email && (
            <MenuItem onClick={() => handleCopyToClipboard(contact?.email as string)}>
              <Iconify icon="mdi:email-outline" />
              <Typography variant='body2'>{t('candidates.detail.actions.copy_email')}</Typography>
            </MenuItem>
          )
        }

        {
          contact?.email && (
            <MenuItem
              onClick={() => {
                optionsPopover.onClose();
                handleSendEmail();
              }}
            >
              <Iconify icon="lsicon:email-send-filled" />
              <Typography variant='body2'>{t('common.send_email')}</Typography>
            </MenuItem>
          )
        }

        {
          tenantType !== TenantType.Candidate && (
            <MenuItem
              onClick={() => {
                optionsPopover.onClose();
                handleCreateCandidate();
              }}
            >
              <Iconify icon="hugeicons:student" />
              <Typography variant='body2'>{t('candidates.create_candidate')}</Typography>
            </MenuItem>
          )
        }

        <MenuItem
          onClick={() => {
            optionsPopover.onClose();
            handleAddTask();
          }}
        >
          <Iconify icon="ic:round-add-task" />
          <Typography variant='body2'>{t('common.add_task')}</Typography>
        </MenuItem>


      </CustomPopover>

    </>
  );
}
